import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Wrench, Building2, Users2, Boxes, FileStack,
  GraduationCap, MonitorSmartphone, Map, ClipboardCheck, FileText,
  Search, Bell, Sun, Moon, LogOut, Menu, ChevronRight, Sparkles, ShieldCheck, HandCoins, ScrollText, UserCircle2
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Chatbot from "@/components/chatbot/Chatbot";

const NAV = [
  { section: "OVERVIEW", items: [
    { to: "/app/dashboard", label: "Executive Dashboard", icon: LayoutDashboard, roles: ["ceo","director","super_admin","admin","engineer","finance","hr","inspector"] },
    { to: "/app/citizen", label: "My Dashboard", icon: LayoutDashboard, roles: ["citizen","industry","farmer","vendor","consultant"] },
    { to: "/app/profile", label: "My Profile", icon: UserCircle2, roles: ["ceo","director","super_admin","admin","engineer","finance","hr","inspector"] },
  ]},
  { section: "OPERATIONS", items: [
    { to: "/app/works", label: "Works Management", icon: Wrench, roles: ["engineer","director","ceo","super_admin","admin"] },
    { to: "/app/inspection", label: "Field Inspection", icon: ClipboardCheck, roles: ["inspector","engineer","director","ceo","super_admin","admin"] },
    { to: "/app/gis", label: "Punjab GIS Map", icon: Map },
  ]},
  { section: "ERP", items: [
    { to: "/app/erp/finance", label: "Finance", icon: HandCoins, roles: ["finance","director","ceo","super_admin","admin"] },
    { to: "/app/erp/hr", label: "HRMS", icon: Users2, roles: ["hr","director","ceo","super_admin","admin"] },
    { to: "/app/erp/inventory", label: "Inventory", icon: Boxes, roles: ["hr","admin","super_admin","director","ceo"] },
    { to: "/app/erp/admin", label: "Administration", icon: Building2, roles: ["admin","super_admin","director","ceo"] },
  ]},
  { section: "KNOWLEDGE", items: [
    { to: "/app/knowledge", label: "Knowledge Base", icon: FileStack },
    { to: "/app/learning", label: "AI Learning", icon: GraduationCap },
    { to: "/app/it-assets", label: "IT Assets", icon: MonitorSmartphone, roles: ["admin","super_admin"] },
  ]},
  { section: "GOVERNANCE", items: [
    { to: "/app/tenders", label: "Tenders", icon: ScrollText },
    { to: "/app/grievances", label: "Grievances & RTI", icon: FileText },
    { to: "/app/security", label: "Access & Audit", icon: ShieldCheck, roles: ["super_admin","admin"] },
  ]},
];

export default function AppShell() {
  const [openMobile, setOpenMobile] = useState(false);
  const [q, setQ] = useState("");
  const { user, logout } = useAuth() || {};
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const role = user?.role;

  const visible = (item) => !item.roles || item.roles.includes(role);

  const submit = (e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q)}`); };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 bg-peda-navyDeep text-white ${openMobile ? "block" : "hidden lg:block"}`}>
        <div className="p-4 border-b border-white/10 flex items-center gap-2">
          <div className="grid place-items-center h-9 w-9 rounded-lg bg-[hsl(var(--primary))] text-white font-bold text-[10px]">PEDA</div>
          <div>
            <div className="text-sm font-semibold">PEDA Portal</div>
            <div className="text-[10px] text-slate-400">Govt. of Punjab</div>
          </div>
        </div>
        <div className="p-3 border-b border-white/10">
          <div className="flex items-center gap-3 rounded-lg bg-white/5 p-2.5">
            <div className="h-9 w-9 rounded-full bg-[hsl(var(--primary))]/20 grid place-items-center text-[hsl(var(--primary))] font-semibold">
              {user?.name?.[0] || "U"}
            </div>
            <div className="text-xs">
              <div className="font-semibold text-white truncate max-w-[130px]">{user?.name}</div>
              <div className="text-slate-400 uppercase text-[10px] tracking-wider">{user?.role}</div>
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-4 overflow-y-auto h-[calc(100vh-160px)]">
          {NAV.map((sec) => {
            const items = sec.items.filter(visible);
            if (!items.length) return null;
            return (
              <div key={sec.section}>
                <div className="label-tiny text-slate-500 px-2 mb-1">{sec.section}</div>
                <div className="space-y-1">
                  {items.map((it) => (
                    <NavLink key={it.to} to={it.to} end={it.to === "/app/dashboard" || it.to === "/app/citizen"} className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                             onClick={()=>setOpenMobile(false)}>
                      <it.icon className="h-4 w-4"/>
                      <span>{it.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
          <button onClick={logout} className="sidebar-link w-full mt-6" data-testid="logout-button">
            <LogOut className="h-4 w-4"/><span>Logout</span>
          </button>
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
          <div className="flex items-center gap-4 px-4 lg:px-8 py-3">
            <button className="lg:hidden p-2 rounded-md hover:bg-muted" onClick={()=>setOpenMobile(!openMobile)}><Menu className="h-5 w-5"/></button>
            <form onSubmit={submit} className="flex-1 max-w-lg hidden sm:flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1.5">
              <Search className="h-4 w-4 opacity-60"/>
              <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search anything…" className="w-full bg-transparent outline-none text-sm" data-testid="app-global-search"/>
            </form>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={toggle} className="p-2 rounded-full hover:bg-muted" aria-label="Toggle theme" data-testid="dark-mode-toggle">
                {theme === "dark" ? <Sun className="h-4 w-4"/> : <Moon className="h-4 w-4"/>}
              </button>
              <button className="p-2 rounded-full hover:bg-muted relative" aria-label="Notifications">
                <Bell className="h-4 w-4"/>
                <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-red-500"/>
              </button>
              <Link to="/" className="btn-outline hidden sm:inline-flex text-xs">Back to Portal</Link>
            </div>
          </div>
          {/* Breadcrumbs */}
          <div className="px-4 lg:px-8 py-2 text-xs text-muted-foreground flex items-center gap-1.5">
            <Link to="/app" className="hover:text-foreground">Home</Link>
            {loc.pathname.split("/").filter(Boolean).slice(1).map((seg, i, arr) => (
              <React.Fragment key={i}>
                <ChevronRight className="h-3 w-3 opacity-60"/>
                <span className={i === arr.length-1 ? "text-foreground font-medium capitalize" : "capitalize"}>{seg.replace(/-/g," ")}</span>
              </React.Fragment>
            ))}
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
      <Chatbot />
    </div>
  );
}
